import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowLeft, MessageCircle, RotateCw } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { cachedMarketIntelligence, storedMarketIntelligence, fetchMarketIntelligence } from './PortfolioView';
import BrainLoader from './BrainLoader';
import CopilotBriefing from './CopilotBriefing';
import { BRAIN_START } from './ChatWidget/ChatWidget';
import './InvestMenterBrain.css';

/**
 * THE INVESTMENTER BRAIN — four pages, one argument.
 *
 *   01  black  Anyone can compare DLD data.        (the owner's units vs DLD)
 *   02  white  Only InvestMenter has its own deal   (anonymised client history,
 *              history.                             /api/dld/deal-history)
 *   03  gold   A normal AI cannot do this.         (questions that open the chat,
 *              The InvestMenter AI can.             which is given both sources)
 *   04  black  Are you a real estate expert?      (why a doctor or lawyer should
 *              Be smart, use the Brain.             not do this alone; opens the chat)
 *
 * Every figure is real: page 1 is the owner's own DLD payload, page 2 is the
 * backend's anonymised aggregate. Nothing on these pages is written in by hand;
 * when a source has nothing to show, the page says so.
 *
 * Built from divs and spans on purpose — index.css carries !important rules on
 * h1/h2/p that bleed into the portals.
 */
interface Props {
  currentInvestor: any;
  units: any[];
  /** Origin + /api. Undefined in demo mode, where the overrides are used. */
  apiUrl?: string;
  onAsk?: (message: string) => void;
  dldOverride?: any;
  historyOverride?: any;
  /** Showcase: Property Management fixtures for the Co-Pilot briefing. */
  pmUnitsOverride?: any[];
}

const DAY_FMT: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

/** Tries `load` up to three times (now, +3s, +8s) until it gives a value. */
async function withRetries<T>(load: () => Promise<T | null>, alive: () => boolean): Promise<T | null> {
  const waits = [3000, 8000];
  for (let i = 0; ; i++) {
    const v = await load().catch(() => null);
    if (v || i >= waits.length || !alive()) return v || null;
    await new Promise((r) => setTimeout(r, waits[i]));
  }
}

// The deal history changes slowly — a copy on the device keeps page 2 filled
// through a cold backend or a dropped connection. Kept PER INVESTOR: the
// "yours" rows are this owner's own prices, and a second person signing in on
// the same phone must never see them.
const HISTORY_KEY = (investorId: string) => `imbDealHistory_${investorId}`;
function readStoredHistory(investorId?: string): any {
  if (!investorId) return null;
  try {
    const raw = localStorage.getItem(HISTORY_KEY(investorId));
    const v = raw ? JSON.parse(raw) : null;
    return v?.totals ? v : null;
  } catch { return null; }
}
function storeHistory(investorId: string | undefined, data: any) {
  if (!investorId) return;
  try { localStorage.setItem(HISTORY_KEY(investorId), JSON.stringify(data)); } catch { /* storage full or blocked */ }
}

const InvestMenterBrain: React.FC<Props> = ({ currentInvestor, units, apiUrl, onAsk, dldOverride, historyOverride, pmUnitsOverride }) => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-AE';
  const whole = (n: number) => Math.round(n).toLocaleString(locale);
  // "AED" as a small prefix, the figure itself never breaking mid-number.
  const money = (n: number) => (
    <span className="imb-money"><span className="imb-cur">AED</span>{whole(n)}</span>
  );
  const investorId = currentInvestor?.id;

  // ── DLD (page 1) ─ memory cache, then the device copy, then a fetch: the
  // owner opened this page on purpose, so waiting for the valuation is fine.
  const [dld, setDld] = useState<any>(() =>
    dldOverride || (investorId ? (cachedMarketIntelligence(investorId) || storedMarketIntelligence(investorId)) : null));
  const [dldLoading, setDldLoading] = useState(false);
  const [dldFailed, setDldFailed] = useState(false);
  const [dldTry, setDldTry] = useState(0);
  useEffect(() => {
    if (dldOverride) { setDld(dldOverride); return; }
    const token = localStorage.getItem('authToken');
    if (!investorId || !apiUrl || !token || cachedMarketIntelligence(investorId)) return;
    let alive = true;
    setDldLoading(true);
    setDldFailed(false);
    // A freshly started backend can drop the first request; two quiet retries
    // before the page admits it has nothing.
    withRetries(() => fetchMarketIntelligence(apiUrl, investorId, token), () => alive)
      .then((d) => { if (!alive) return; if (d) setDld(d); else setDldFailed(true); })
      .finally(() => { if (alive) setDldLoading(false); });
    return () => { alive = false; };
  }, [investorId, apiUrl, dldOverride, dldTry]);

  // ── Deal history (page 2) ─ the last good copy from this device first, so
  // the page is never blank, then a fresh one from the backend. A failed
  // fetch is shown as a failure with a retry, never as "no deals".
  const [history, setHistory] = useState<any>(() => historyOverride || readStoredHistory(investorId));
  const [historyLoading, setHistoryLoading] = useState(!historyOverride);
  const [historyFailed, setHistoryFailed] = useState(false);
  const [historyTry, setHistoryTry] = useState(0);
  useEffect(() => {
    if (historyOverride) { setHistory(historyOverride); setHistoryLoading(false); return; }
    const token = localStorage.getItem('authToken');
    if (!apiUrl || !token) { setHistoryLoading(false); return; }
    let alive = true;
    setHistoryLoading(true);
    setHistoryFailed(false);
    const load = () => fetch(`${apiUrl}/dld/deal-history`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => (j?.success ? j.data : null))
      .catch(() => null);
    withRetries(load, () => alive)
      .then((data) => {
        if (!alive) return;
        if (data) { setHistory(data); storeHistory(investorId, data); } else setHistoryFailed(true);
      })
      .finally(() => { if (alive) setHistoryLoading(false); });
    return () => { alive = false; };
  }, [apiUrl, investorId, historyOverride, historyTry]);

  // ── Pager ─ native horizontal scroll with snap, so a swipe works on a phone
  // and the buttons/dots just scroll it.
  const track = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(0);
  const go = (i: number) => {
    const el = track.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };
  const onScroll = () => {
    const el = track.current;
    if (el && el.clientWidth) setPage(Math.round(el.scrollLeft / el.clientWidth));
  };
  // The track takes the height of the page on screen. Left to flexbox, every
  // page stretches to the tallest one and the short pages end in a blank slab.
  const [trackH, setTrackH] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = track.current;
    const cur = el?.children[page] as HTMLElement | undefined;
    if (!cur) return;
    const measure = () => setTrackH(cur.offsetHeight);
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(cur);
    return () => ro?.disconnect();
  }, [page]);

  // Page 1 rows: held units with a price, joined to their DLD valuation.
  const byId = new Map<string, any>();
  (dld?.units || []).forEach((x: any) => { if (x?.unitId) byId.set(String(x.unitId), x); });
  const held = (units || []).filter((u: any) =>
    (u?.status || '').toLowerCase() !== 'sold' && (Number(u?.purchaseValue) || 0) > 0);
  const rows = held.map((u: any) => {
    const x = byId.get(String(u.id));
    const paid = Number(u.purchaseValue) || 0;
    const value = Number(x?.dldMarketValue) || 0;
    return {
      id: String(u.id),
      name: u.project || u.name || '—',
      sub: u.project && u.name && u.name !== u.project ? u.name : '',
      paid,
      value,
      diff: value > 0 && paid > 0 ? ((value - paid) / paid) * 100 : null,
      comps: x?.comparablesCount,
      low: ['LOW', 'INSUFFICIENT_DATA'].includes(String(x?.confidence || '').toUpperCase()),
    };
  }).sort((a: any, b: any) => (b.value || b.paid) - (a.value || a.paid));
  const valued = rows.filter((r: any) => r.value > 0);
  const paidSum = valued.reduce((s: number, r: any) => s + r.paid, 0);
  const valueSum = valued.reduce((s: number, r: any) => s + r.value, 0);
  const syncDate = dld?.lastSync && !isNaN(Date.parse(dld.lastSync))
    ? new Date(dld.lastSync).toLocaleDateString(locale, DAY_FMT)
    : null;

  const tot = history?.totals;
  const yours: any[] = history?.yours || [];
  const topProjects: any[] = (history?.projects || []).slice(0, 6);
  const sinceYear = tot?.firstDeal ? String(tot.firstDeal).slice(0, 4) : null;

  // Page 3: questions that genuinely need both sources. The first names the
  // owner's biggest project that page 2 can compare, when there is one.
  const askProject = yours[0]?.project;
  const questions = [
    askProject ? t('imbAsk1').replace('{project}', askProject) : t('imbAsk1Generic'),
    t('imbAsk2'),
    t('imbAsk3'),
  ];

  const sign = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '');

  return (
    <div className="imb">
      {/* The tab opens with the Co-Pilot itself (Michael, 2026-10-15): the
          owner's own situation first, then the pages on why the numbers can
          be trusted. Same component and data as the briefing on Home. */}
      <CopilotBriefing
        investorName={currentInvestor?.name}
        investorId={currentInvestor?.id}
        units={units}
        dldIntel={dld}
        pmUnitsOverride={pmUnitsOverride}
        onAsk={(m) => onAsk?.(m)}
      />
      <div className="imb-track" ref={track} onScroll={onScroll} style={{ height: trackH }}>
        {/* ── 01 · black · DLD ─────────────────────────────────────────── */}
        <section className="imb-page imb-ink" aria-label={t('imbPage1Title')}>
          <div className="imb-eyebrow">{t('imbPage1Eyebrow')}</div>
          <div className="imb-title">{t('imbPage1Title')}</div>
          <div className="imb-lead">{t('imbPage1Lead')}</div>

          {valued.length > 0 && (
            <div className="imb-sum">
              <div>
                <span className="imb-sum-k">{t('imbPaid')}</span>
                <span className="imb-sum-v">{money(paidSum)}</span>
              </div>
              <div>
                <span className="imb-sum-k">{t('imbDld')}</span>
                <span className="imb-sum-v imb-gold">{money(valueSum)}</span>
              </div>
              <div>
                <span className="imb-sum-k">{t('imbDiff')}</span>
                <span className="imb-sum-v">
                  {sign(valueSum - paidSum)}{Math.abs(((valueSum - paidSum) / (paidSum || 1)) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          <div className="imb-rows">
            {rows.length === 0 && <div className="imb-note">{t('imbNoUnits')}</div>}
            {rows.length > 0 && !dld && (
              dldLoading
                ? <BrainLoader task="imbTaskValuation" compact dark />
                : (
                  <div className="imb-fail">
                    <span>{t('imbDldNone')}</span>
                    {dldFailed && (
                      <button className="imb-btn imb-btn-line" onClick={() => setDldTry((n) => n + 1)}>
                        <RotateCw size={15} /> {t('imbRetry')}
                      </button>
                    )}
                  </div>
                )
            )}
            {dld && rows.slice(0, 6).map((r: any) => (
              <div className="imb-row" key={r.id}>
                <div className="imb-row-name">
                  <span>{r.name}</span>
                  {r.sub && <em>{r.sub}</em>}
                </div>
                <div className="imb-row-fig">
                  <span className="imb-row-k">{t('imbPaid')}</span>
                  <span>{money(r.paid)}</span>
                </div>
                <div className="imb-row-fig">
                  <span className="imb-row-k">{t('imbDld')}</span>
                  {r.value > 0
                    ? <span className="imb-gold">{money(r.value)}</span>
                    : <span className="imb-dim">{t('imbNoDld')}</span>}
                </div>
                <div className="imb-row-tag">
                  {r.diff != null && <b>{sign(r.diff)}{Math.abs(r.diff).toFixed(1)}%</b>}
                  {r.comps != null && <span>{t('imbComps').replace('{n}', String(r.comps))}</span>}
                  {r.low && <span>{t('imbConfLow')}</span>}
                </div>
              </div>
            ))}
            {dld && rows.length > 6 && (
              <div className="imb-note">{t('imbMoreUnits').replace('{n}', String(rows.length - 6))}</div>
            )}
          </div>

          <div className="imb-src">
            {syncDate ? t('imbDldSource').replace('{date}', syncDate) : t('imbDldSourceNoDate')}
          </div>
          <div className="imb-nav">
            <span />
            <button className="imb-btn imb-btn-gold" onClick={() => go(1)}>
              {t('imbNext')} <ArrowRight size={16} />
            </button>
          </div>
        </section>

        {/* ── 02 · white · Asset Desk deal history ───────────────────── */}
        <section className="imb-page imb-paper" aria-label={t('imbPage2Title')}>
          <div className="imb-eyebrow">{t('imbPage2Eyebrow')}</div>
          <div className="imb-title">{t('imbPage2Title')}</div>
          <div className="imb-lead">{t('imbPage2Lead')}</div>

          {historyLoading && !tot && <BrainLoader task="imbTaskDealHistory" compact />}
          {!historyLoading && !tot && historyFailed && (
            <div className="imb-fail">
              <span>{t('imbHistError')}</span>
              <button className="imb-btn imb-btn-line" onClick={() => setHistoryTry((n) => n + 1)}>
                <RotateCw size={15} /> {t('imbRetry')}
              </button>
            </div>
          )}
          {!historyLoading && !historyFailed && (!tot || !tot.deals) && <div className="imb-note">{t('imbHistNone')}</div>}

          {tot && tot.deals > 0 && (
            <>
              <div className="imb-stats">
                <div><span className="imb-stat-v">{whole(tot.deals)}</span><span className="imb-stat-k">{t('imbDeals')}</span></div>
                <div><span className="imb-stat-v">{whole(tot.clients)}</span><span className="imb-stat-k">{t('imbClients')}</span></div>
                <div><span className="imb-stat-v">{whole(tot.projects)}</span><span className="imb-stat-k">{t('imbProjects')}</span></div>
              </div>
              <div className="imb-stat-line">
                AED {whole(tot.volume)} {t('imbBought')}
                {sinceYear && <> · {t('imbSince').replace('{year}', sinceYear)}</>}
                {tot.medianAnnualisedRoi != null && (
                  <> · {t('imbResaleAll').replace('{roi}', String(tot.medianAnnualisedRoi))}</>
                )}
              </div>

              {yours.length > 0 && (
                <div className="imb-block">
                  <div className="imb-block-t">{t('imbYouVsClients')}</div>
                  {yours.slice(0, 5).map((y: any) => (
                    <div className="imb-vs" key={y.key}>
                      <div className="imb-vs-name">
                        <span>{y.project}</span>
                        <em>{t('imbDealsBy').replace('{deals}', String(y.clientDeals)).replace('{clients}', String(y.clients))}</em>
                      </div>
                      <div className="imb-vs-figs">
                        <div>
                          <span className="imb-row-k">{t('imbYouPaid')}</span>
                          <span>{y.yourPsf ? `AED ${whole(y.yourPsf)}${t('imbPerSqft')}` : '—'}</span>
                        </div>
                        <div>
                          <span className="imb-row-k">{t('imbClientsMedian')}</span>
                          <span>{y.clientsPsf ? `AED ${whole(y.clientsPsf)}${t('imbPerSqft')}` : '—'}</span>
                        </div>
                        {y.diffPct != null && (
                          <b className={y.diffPct <= 0 ? 'imb-good' : ''}>{sign(y.diffPct)}{Math.abs(y.diffPct).toFixed(1)}%</b>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {topProjects.length > 0 && (
                <div className="imb-block">
                  <div className="imb-block-t">{t('imbTopProjects')}</div>
                  {topProjects.map((p: any) => (
                    <div className="imb-proj" key={p.key}>
                      <span className="imb-proj-name">{p.project}</span>
                      <span className="imb-proj-meta">
                        {t('imbDealsBy').replace('{deals}', String(p.deals)).replace('{clients}', String(p.clients))}
                        {p.medianPsf ? ` · AED ${whole(p.medianPsf)}${t('imbPerSqft')}` : ''}
                        {p.medianAnnualisedRoi != null ? ` · ${t('imbResale').replace('{roi}', String(p.medianAnnualisedRoi))}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="imb-src">
            {t('imbPrivacy').replace('{n}', String(history?.minClients || 3))}
            {historyFailed && tot && history?.generatedAt && !isNaN(Date.parse(history.generatedAt)) && (
              <> {t('imbHistAsOf').replace('{date}', new Date(history.generatedAt).toLocaleDateString(locale, DAY_FMT))}</>
            )}
          </div>
          <div className="imb-nav">
            <button className="imb-btn imb-btn-line" onClick={() => go(0)}>
              <ArrowLeft size={16} /> {t('imbBack')}
            </button>
            <button className="imb-btn imb-btn-ink" onClick={() => go(2)}>
              {t('imbNext')} <ArrowRight size={16} />
            </button>
          </div>
        </section>

        {/* ── 03 · gold · the claim ────────────────────────────────────── */}
        <section className="imb-page imb-goldpage" aria-label={t('imbPage3Title')}>
          <div className="imb-eyebrow">{t('imbPage3Eyebrow')}</div>
          <div className="imb-kicker">{t('imbPage3Kicker')}</div>
          <div className="imb-title imb-title-xl">{t('imbPage3Title')}</div>
          <div className="imb-lead">{t('imbPage3Lead')}</div>

          <div className="imb-asks">
            <div className="imb-block-t">{t('imbAskTitle')}</div>
            {questions.map((q) => (
              <button key={q} className="imb-ask" onClick={() => onAsk?.(q)} disabled={!onAsk}>
                <MessageCircle size={18} />
                <span>{q}</span>
                <ArrowRight size={16} />
              </button>
            ))}
          </div>

          <div className="imb-nav">
            <button className="imb-btn imb-btn-line" onClick={() => go(1)}>
              <ArrowLeft size={16} /> {t('imbBack')}
            </button>
            <button className="imb-btn imb-btn-ink" onClick={() => go(3)}>
              {t('imbNext')} <ArrowRight size={16} />
            </button>
          </div>
        </section>

        {/* ── 04 · black · are you the expert? ─────────────────────────── */}
        <section className="imb-page imb-ink" aria-label={t('imbPage4Kicker')}>
          <div className="imb-eyebrow">{t('imbPage4Eyebrow')}</div>
          <div className="imb-title">{t('imbPage4Kicker')}</div>
          <div className="imb-kicker imb-gold">{t('imbPage4Title')}</div>
          <div className="imb-lead">{t('imbPage4Lead')}</div>

          <div className="imb-block">
            <div className="imb-block-t">{t('imbMissTitle')}</div>
            {['imbMiss1', 'imbMiss2', 'imbMiss3'].map((k, i) => (
              <div className="imb-miss" key={k}>
                <span className="imb-miss-n">{i + 1}</span>
                <span>{t(k)}</span>
              </div>
            ))}
          </div>

          <div className="imb-close">{t('imbClose')}</div>
          <button className="imb-ask imb-ask-gold" onClick={() => onAsk?.(BRAIN_START)} disabled={!onAsk}>
            <MessageCircle size={18} />
            <span>{t('imbCta')}</span>
            <ArrowRight size={16} />
          </button>

          <div className="imb-nav">
            <button className="imb-btn imb-btn-line" onClick={() => go(2)}>
              <ArrowLeft size={16} /> {t('imbBack')}
            </button>
            <span />
          </div>
        </section>
      </div>

      <div className="imb-dots" role="tablist">
        {[0, 1, 2, 3].map((i) => (
          <button
            key={i}
            role="tab"
            aria-selected={page === i}
            aria-label={`${i + 1} / 4`}
            className={`imb-dot ${page === i ? 'on' : ''}`}
            onClick={() => go(i)}
          />
        ))}
      </div>
    </div>
  );
};

export default InvestMenterBrain;
